import type { TypedDocumentNode } from '@apollo/client';
import { gql, useMutation } from '@apollo/client';
import { faDownToLine } from '@fortawesome/pro-solid-svg-icons';
import { subYears } from 'date-fns';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';

import { AccountDialog } from '#src/components/mySorare/TransactionsHistory/AccountDialog/index.tsx';

import type {
  GenerateAccountingExtractMutation,
  GenerateAccountingExtractMutationVariables,
} from './__generated__/index.graphql.ts';

const GENERATE_ACCOUNTING_EXTRACT_MUTATION = gql`
  mutation GenerateAccountingExtractMutation(
    $input: generateAccountingExtractInput!
  ) {
    generateAccountingExtract(input: $input) {
      errors {
        code
        message
        path
      }
    }
  }
` as TypedDocumentNode<
  GenerateAccountingExtractMutation,
  GenerateAccountingExtractMutationVariables
>;

export const AccountExtractButton = () => {
  const { showNotification } = useSnackNotificationContext();
  const [accountDialogOpened, setAccountDialogOpened] = useState(false);
  const [startDate, setStartDate] = useState<Date>(subYears(new Date(), 1));
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [generate, { loading }] = useMutation(
    GENERATE_ACCOUNTING_EXTRACT_MUTATION
  );

  const generateAccountExtract = () => {
    generate({
      variables: {
        input: {
          includeFiatEntries: true,
          includeCreditCardPayments: true,
          includeLostBids: true,
          includeCardRewards: true,
          startDate,
          endDate,
        },
      },
    }).then(({ data }) => {
      if (!data?.generateAccountingExtract?.errors.length) {
        showNotification('accountingExtractSent');
      }
    });
  };

  return (
    <LoadingButton
      color="tertiary"
      size="small"
      startIcon={<FontAwesomeIcon icon={faDownToLine} />}
      onClick={() => setAccountDialogOpened(true)}
      loading={loading}
    >
      <FormattedMessage defaultMessage="CSV" />
      <AccountDialog
        accountDialogOpened={accountDialogOpened}
        setAccountDialogOpened={setAccountDialogOpened}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        generateAccountExtract={generateAccountExtract}
        loading={loading}
      />
    </LoadingButton>
  );
};
